import { DropDown, Icon } from '@aibees/shared/components';
import { MemberStatus } from '@aibees/shared/definition';
import { classnames } from '@aibees/shared/utils';
import { useWorkspace } from '@aibees/shared/workspace';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { capitalize } from 'lodash-es';
import { memo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import * as Styled from './styles';
import * as SX from './sx';
import type { ChangeOwnershipFormProps as Props } from './types';

export const ChangeOwnershipForm = memo(({ className }: Props) => {
  const workspace = useWorkspace();
  const { watch, control } = useFormContext();

  const options =
    workspace?.members
      .filter(
        (member) =>
          member.status === MemberStatus.Active &&
          member.user?.id !== workspace?.user?.id &&
          member.user?.id
      )
      .map((member) => ({
        key: member.user!.id,
        value: member.user!.id,
        label: `${member.user!.firstName} ${member.user!.lastName}, ${
          member.user!.username
        }`,
        member,
        memberType: capitalize(member.type),
      })) ?? [];

  const optionsWithTypeInLabel = options.map((option) => ({
    ...option,
    label: option.label,
    metadata: `(${option.memberType})`,
  }));

  const renderItem = (label: string, option: any) => (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{ width: '100%' }}
    >
      <Typography variant="body4" sx={{ flex: 1 }}>
        {label}
      </Typography>
      <Typography variant="body4" color="grey.600">
        ({option.memberType})
      </Typography>
    </Box>
  );

  const renderValue = (value: any) => {
    const selectedOption = options.find((option) => option.value === value);
    if (!selectedOption) return null;

    return (
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ width: '100%' }}
      >
        <Typography
          variant="body4"
          sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {selectedOption.label}
        </Typography>
        <Typography
          variant="body4"
          color="grey.600"
          sx={{ ml: 2, flexShrink: 0 }}
        >
          ({selectedOption.memberType})
        </Typography>
      </Box>
    );
  };

  return (
    <Styled.Wrapper
      className={classnames('ChangeOwnershipForm', className)}
      sx={SX.root}
      gap={1}
    >
      <Stack gap={1}>
        <Typography variant="body4" color="grey.600">
          As the current owner, you have the authority to select one of your
          team members for the transfer of new ownership. All pertinent
          information will be seamlessly transferred to the chosen new owner.
        </Typography>
        <Typography variant="body4" color="grey.600">
          Your Role will be automatically turned to Admin which you can then
          change after transfer to member or deactivate your account.
        </Typography>
        <Divider sx={SX.dashed} />
      </Stack>
      <Typography variant="body4" color="grey.500">
        Transfer Ownership To
      </Typography>
      <Controller
        name="newOwner"
        control={control}
        render={({ field }) => (
          <DropDown
            {...field}
            options={optionsWithTypeInLabel}
            placeholder="Choose a team member"
            size="small"
            variant="outlined"
            startAdornment={
              <Icon name="published-with-changes" color="grey.600" size={20} />
            }
            renderItem={renderItem}
            renderValue={renderValue}
          />
        )}
      />
      <Typography variant="body4" color="grey.600">
        All records will be assigned to the new owner.
      </Typography>
    </Styled.Wrapper>
  );
});

ChangeOwnershipForm.displayName = 'ChangeOwnershipForm';
